import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { format, formatDistanceToNow } from "date-fns";
import { 
  Search, 
  Filter, 
  ArrowRight, 
  User, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Edit, 
  MessageSquare,
  Zap
} from "lucide-react";
import { useBillActivityLogs } from "@/hooks/useBillActivityLogs";
import type { BillStage } from '@/lib/stage-utils';

interface StageActivityLogsProps {
  stage?: BillStage;
  billId?: string;
}

export function StageActivityLogs({ stage, billId }: StageActivityLogsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [actionTypeFilter, setActionTypeFilter] = useState<string>("all");

  const { data: logs = [], isLoading } = useBillActivityLogs({
    stage,
    billId,
    actionType: actionTypeFilter,
    days: 15
  });

  // Filter logs by search term
  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      log.description?.toLowerCase().includes(searchLower) ||
      log.action?.toLowerCase().includes(searchLower) ||
      log.user_name?.toLowerCase().includes(searchLower)
    );
  });

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'stage_change': return <ArrowRight className="h-4 w-4" />;
      case 'assignment': return <User className="h-4 w-4" />;
      case 'parsing': return <Zap className="h-4 w-4" />;
      case 'validation': return <CheckCircle className="h-4 w-4" />;
      case 'approval': return <CheckCircle className="h-4 w-4" />;
      case 'rejection': return <XCircle className="h-4 w-4" />;
      case 'edit': return <Edit className="h-4 w-4" />;
      case 'note': return <MessageSquare className="h-4 w-4" />;
      case 'system': return <Zap className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'stage_change': return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200';
      case 'assignment': return 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200';
      case 'parsing': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200';
      case 'validation': return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200';
      case 'approval': return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200';
      case 'rejection': return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200';
      case 'edit': return 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200';
      case 'note': return 'bg-muted text-muted-foreground';
      case 'system': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Activity & Logs</CardTitle>
              <CardDescription>
                {stage 
                  ? `Activity logs for ${stage} stage (last 15 days)`
                  : `All activity logs (last 15 days)`
                }
              </CardDescription>
            </div>
            <Badge variant="outline">{filteredLogs.length} events</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="stage_change">Stage Changes</SelectItem>
                <SelectItem value="assignment">Assignments</SelectItem>
                <SelectItem value="parsing">Parsing</SelectItem>
                <SelectItem value="validation">Validation</SelectItem>
                <SelectItem value="approval">Approvals</SelectItem>
                <SelectItem value="rejection">Rejections</SelectItem>
                <SelectItem value="edit">Edits</SelectItem>
                <SelectItem value="note">Notes</SelectItem>
                <SelectItem value="system">System Events</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Activity Timeline */}
          <ScrollArea className="h-[600px] pr-4">
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                Loading activity logs...
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No activity logs found</p>
                {searchTerm && <p className="text-sm mt-2">Try adjusting your search</p>}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLogs.map((log, index) => (
                  <div key={log.id} className="flex gap-4">
                    {/* Timeline Line */}
                    <div className="flex flex-col items-center">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${getActionColor(log.action_type)}`}>
                        {getActionIcon(log.action_type)}
                      </div>
                      {index < filteredLogs.length - 1 && (
                        <div className="w-0.5 flex-1 bg-border my-2" />
                      )}
                    </div>

                    {/* Activity Content */}
                    <div className="flex-1 pb-6">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {log.user_name && (
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {log.user_name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <span className="font-medium text-sm">
                            {log.user_name || 'System'}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {log.action_type.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-2">
                        {log.description}
                      </p>

                      {log.bill_id && (
                        <div className="text-xs text-muted-foreground">
                          Bill: <span className="font-mono">{log.bill_id.substring(0, 8)}</span>
                        </div>
                      )}

                      {/* Show old/new values for edits */}
                      {(log.old_value || log.new_value) && (
                        <div className="mt-2 p-2 bg-muted rounded text-xs space-y-1">
                          {log.old_value && (
                            <div>
                              <span className="font-medium">Before: </span>
                              <span className="text-muted-foreground">
                                {JSON.stringify(log.old_value)}
                              </span>
                            </div>
                          )}
                          {log.new_value && (
                            <div>
                              <span className="font-medium">After: </span>
                              <span className="text-green-600 dark:text-green-400">
                                {JSON.stringify(log.new_value)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-xs text-muted-foreground mt-2">
                        {format(new Date(log.created_at), 'MMM dd, yyyy @ h:mm a')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
